if (!customElements.get('pe-hero-slider')) {
  class PeHeroSlider extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.abort = new AbortController();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.abort.signal });
      } else {
        this.initFrame = requestAnimationFrame(() => this.initialize());
      }
    }
    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (!this.track || !this.slides.length) return;
      this.initialized = true;
      const signal = this.abort.signal;
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.progress = this.querySelector('[data-progress-fill]');
      this.index = 0;
      this.targetIndex = null;
      this.visible = true;
      this.dragging = false;
      this.moving = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.duration = Math.max(5, Number(this.dataset.autoplay) || 7) * 1000;
      this.remaining = this.duration;
      this.trackWidth = this.track.clientWidth;
      this.dataset.direction = getComputedStyle(this).direction;
      const controls = this.querySelector('[data-controls]');
      if (controls) controls.hidden = this.slides.length < 2;
      const progress = this.querySelector('[data-progress]');
      if (progress) progress.hidden = this.slides.length < 2 || !this.dataset.autoplay;
      this.querySelectorAll('[data-prev],[data-next],[data-dot]').forEach(button => {
        button.addEventListener('click', () => {
          const current = this.targetIndex ?? this.index;
          this.go(button.hasAttribute('data-dot') ? Number(button.dataset.dot) : current + (button.hasAttribute('data-next') ? 1 : -1));
        }, { signal });
      });
      this.track.addEventListener('scroll', () => {
        this.resetClock();
        this.moving = true;
        this.syncClock();
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
        this.waitForSettled();
      }, { passive: true, signal });
      this.track.addEventListener('scrollend', () => this.finish(), { signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.go((this.targetIndex ?? this.index) + ((event.key === 'ArrowLeft') === (this.dataset.direction === 'rtl') ? 1 : -1));
      }, { signal });
      this.track.addEventListener('pointerdown', () => {
        this.dragging = true;
        this.targetIndex = null;
        this.syncClock();
      }, { passive: true, signal });
      const release = () => {
        if (!this.dragging) return;
        this.dragging = false;
        if (this.moving) this.waitForSettled();
        else this.syncClock();
      };
      document.addEventListener('pointerup', release, { passive: true, signal });
      document.addEventListener('pointercancel', release, { passive: true, signal });
      document.addEventListener('visibilitychange', () => {
        this.dragging = false;
        if (document.hidden) this.syncClock();
        else { this.resetClock(); this.finish(); }
      }, { signal });
      window.addEventListener('pagehide', () => this.suspendClock(), { signal });
      window.addEventListener('pageshow', () => {
        this.dragging = false;
        this.resetClock();
        this.finish();
      }, { signal });
      this.motion.addEventListener('change', () => {
        if (this.moving) this.go(this.targetIndex ?? this.index, 'instant');
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.go(index, 'instant');
      }, { signal });
      if (typeof IntersectionObserver !== 'undefined') {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          if (!this.visible) this.dragging = false;
          this.syncClock();
        }, { threshold: .01 });
        this.observer.observe(this);
      }
      const resize = width => {
        if (width > 0 && Math.abs(width - this.trackWidth) > 1) {
          this.trackWidth = width;
          this.go(this.targetIndex ?? this.index, 'instant');
        }
      };
      if (typeof ResizeObserver !== 'undefined') {
        this.resize = new ResizeObserver(entries => resize(entries[0].contentRect.width));
        this.resize.observe(this.track);
      } else window.addEventListener('resize', () => resize(this.track.clientWidth), { signal });
      this.update();
      this.track.setAttribute('aria-live', 'off');
      this.preloadNext();
      this.dataset.ready = 'true';
      this.syncClock();
    }
    go(index, behavior) {
      this.resetClock();
      const target = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      const movement = behavior || (this.motion.matches || this.moving || Math.abs(target - this.index) > 1 ? 'instant' : 'smooth');
      this.targetIndex = target;
      const bounds = this.track.getBoundingClientRect();
      const slide = this.slides[target].getBoundingClientRect();
      const delta = this.dataset.direction === 'rtl' ? slide.right - bounds.right : slide.left - bounds.left;
      this.moving = true;
      this.syncClock();
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: movement });
      if (movement === 'instant' || Math.abs(delta) < 1) this.finish();
      else this.waitForSettled();
    }
    nearestIndex() {
      const bounds = this.track.getBoundingClientRect();
      let nearest = Infinity;
      let current = this.index;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(this.dataset.direction === 'rtl' ? rect.right - bounds.right : rect.left - bounds.left);
        if (distance < nearest) { nearest = distance; current = index; }
      });
      return current;
    }
    update() {
      this.index = this.nearestIndex();
      this.slides.forEach((slide, index) => {
        slide.inert = index !== this.index;
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.dots.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.dot) === this.index)));
    }
    preloadNext() {
      this.slides[(this.index + 1) % this.slides.length].querySelector('img')?.setAttribute('loading', 'eager');
    }
    waitForSettled() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.finish(), 200);
    }
    finish() {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.update();
      this.targetIndex = null;
      this.moving = false;
      this.preloadNext();
      this.syncClock();
    }
    suspendClock() {
      if (this.timer != null && this.deadline != null) this.remaining = Math.max(0, this.deadline - performance.now());
      clearTimeout(this.timer);
      this.timer = null;
      this.deadline = null;
      this.renderProgress();
    }
    resetClock() {
      this.suspendClock();
      this.remaining = this.duration;
      this.renderProgress();
    }
    renderProgress(running = false) {
      if (!this.progress) return;
      this.progress.style.transition = 'none';
      this.progress.style.transform = `scaleX(${Math.max(0, Math.min(1, 1 - this.remaining / this.duration))})`;
      if (!running) return;
      void this.progress.offsetWidth;
      this.progress.style.transition = `transform ${this.remaining}ms linear`;
      this.progress.style.transform = 'scaleX(1)';
    }
    canRotate() {
      return this.isConnected && this.dataset.autoplay && this.slides.length > 1 && this.visible && !this.dragging && !this.moving && !document.hidden;
    }
    syncClock() {
      if (!this.canRotate()) {
        this.suspendClock();
        this.dataset.autoplayState = this.dataset.autoplay && this.slides.length > 1 ? 'suspended' : 'idle';
        return;
      }
      if (this.timer != null) return;
      const delay = this.remaining || this.duration;
      this.remaining = delay;
      this.dataset.autoplayState = 'playing';
      this.deadline = performance.now() + delay;
      this.timer = setTimeout(() => {
        this.timer = null;
        this.deadline = null;
        this.remaining = this.duration;
        if (this.canRotate()) this.go(this.index + 1);
        else this.syncClock();
      }, delay);
      this.renderProgress(true);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.initFrame);
      if (this.progress) this.progress.style.transition = 'none';
      this.timer = null;
      this.deadline = null;
      this.initialized = false;
    }
  }
  customElements.define('pe-hero-slider', PeHeroSlider);
}
